import { LineChart } from '@mui/x-charts/LineChart';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import type { EnergySeries } from './types';

const SERIES_COLORS: Record<string, string> = {
  'Electricity (kWh)': '#1D9E75',
  'Gas (m³)':          '#BA7517',
  'Cost (£)':          '#D4537E',
};

const LEGEND_ITEMS = [
  { label: 'Electricity (kWh)', color: '#1D9E75', dashed: false },
  { label: 'Gas (m³)',          color: '#BA7517', dashed: false },
  { label: 'Water (m³)',        color: '#378ADD', dashed: false },
  { label: 'Cost (£)',          color: '#D4537E', dashed: true  },
];

interface Props {
  series:  EnergySeries;
  buckets: string[];
}

export default function EnergyChart({ series, buckets }: Props) {

  const xData = series.labels.map((_, i) => i);

  return (
    <Box sx={styles.panel}>
      <Typography sx={styles.title}>Energy consumption</Typography>

      <LineChart
        xAxis={[{
          data:           xData,
          valueFormatter: (i: number, context) => {
            const bucket = series.labels[i] ?? '';
            if (context.location === 'tooltip') {
              const raw = buckets[i];
              if (raw) {
                return new Date(raw).toLocaleString('en-GB', {
                  weekday: 'short', day: 'numeric', month: 'short',
                  hour: '2-digit', minute: '2-digit',
                });
              }
            }
            return bucket;
          },
          tickLabelStyle: { fontSize: 11 },
          scaleType:      'point',
        }]}
        yAxis={[
          {
            id:             'energy',
            tickLabelStyle: { fontSize: 11 },
          },
          {
            id:             'cost',
            tickLabelStyle: { fontSize: 11 },
          },
        ]}
        leftAxis="energy"
        rightAxis="cost"
        series={[
          {
            id:       'electricity',
            label:    'Electricity (kWh)',
            data:     series.electricity,
            color:    '#1D9E75',
            yAxisId:  'energy',
            area:     true,
            showMark: series.electricity.length <= 14,
            curve:    'catmullRom',
          },
          {
            id:       'gas',
            label:    'Gas (m³)',
            data:     series.gas,
            color:    '#BA7517',
            yAxisId:  'energy',
            area:     true,
            showMark: series.gas.length <= 14,
            curve:    'catmullRom',
          },
          {
            id:       'water',
            label:    'Water (m³)',
            data:     series.water,
            color:    '#378ADD',
            yAxisId:  'energy',
            area:     true,
            showMark: series.water.length <= 14,
            curve:    'catmullRom',
          },
          {
            id:       'cost',
            label:    'Cost (£)',
            data:     series.cost,
            color:    '#D4537E',
            yAxisId:  'cost',
            showMark: false,
            curve:    'catmullRom',
          },
        ]}
        grid={{ horizontal: true }}
        height={210}
        margin={{ top: 10, right: 48, bottom: 30, left: 44 }}
        slotProps={{ legend: { hidden: true } }}
        sx={{
          '& .MuiAreaElement-root[data-id="electricity"]': {
            fill: 'rgba(29,158,117,0.08)',
          },
          '& .MuiAreaElement-root[data-id="gas"]': {
            fill: 'rgba(186,117,23,0.06)',
          },
          '& .MuiAreaElement-root[data-id="water"]': {
            fill: 'rgba(55,138,221,0.08)',
          },
          '& .MuiLineElement-root[data-id="cost"]': {
            strokeDasharray: '4 3',
            strokeWidth:     1.5,
          },
          '& .MuiMarkElement-root': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
}

const styles = {
  panel: {
    bgcolor:      'var(--color-bg-secondary)',
    borderRadius: 3,
    p:            '1.1rem 1.25rem',
  },
  title: {
    fontSize:   13,
    fontWeight: 600,
    color:      'var(--color-text-primary)',
    mb:         '2px',
  },
  sub: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-primary)',
    mb:         1,
  },
  legendLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize:   11,
    color:      'var(--color-text-primary)',
  },
} as const;
